import { Accessor } from 'solid-js';
import { Web3DViewer } from '@technology/web3d';
import {
  type LineEditingModel,
  type LineEditingPlugin,
} from '@technology/web3d-plugin-line-editing';
import { sampleClosestPointCloudElevation } from '@trimble/web3d-providers';
import {
  DrawingTool,
  FeatureCadTransformer,
  createCadGeometryReader,
  type LonLatProjector,
} from '@trimble/geospatial';
import type { ILogger } from '@trimble/logging';

import type { FeatureExtractionContextType } from '@mobile-mapping/providers';
import {
  CAD_EDITING_MODEL_ID,
  LINE_STYLE_COLOR,
  POINT_DRAWING_STYLE,
  POINT_STYLE_COLOR,
} from './feature-extraction-cad-config';
import { createCadSceneProjection } from './cad-scene-projection';
import { createCadDrawingTools } from './cad-drawing-tools';
import { createCadSelectionSync } from './cad-selection-sync';
import { createFeatureCadMirror } from './feature-cad-mirror';
import { createFeatureEditingBridge } from './feature-editing-bridge';
import { createFeatureServiceModelLoader } from './feature-service-model-loader';
import { createPointCloudCrsAuthoring } from './point-cloud-crs-authoring';
import type { PointCloudRoot } from './feature-extraction-types';

export type { PointCloudRoot };

export interface FeatureExtractionController {
  readonly activeTool: Accessor<DrawingTool | undefined>;
  toggleTool(tool: DrawingTool): void;
  cancelActiveTool(): void;
  dispose(): void;
}

export interface CreateFeatureExtractionControllerParams {
  readonly viewer: Web3DViewer;
  readonly lineEditingPlugin: LineEditingPlugin;
  readonly context: FeatureExtractionContextType;
  readonly logger: ILogger;
  readonly csib: string;
  readonly geoDataFiles: Map<string, ArrayBuffer>;
  readonly pointCloudRoots: Accessor<PointCloudRoot[]>;
}

export function createFeatureExtractionController(
  params: CreateFeatureExtractionControllerParams
): FeatureExtractionController {
  const { viewer, lineEditingPlugin, context } = params;
  const logger = params.logger.createNamedLogger('FeatureExtractionController');
  const cadSceneProjection = createCadSceneProjection({
    csib: params.csib,
    geoDataFiles: params.geoDataFiles,
    logger,
  });

  const model: LineEditingModel = lineEditingPlugin.createModel({
    modelId: CAD_EDITING_MODEL_ID,
  });
  model.setDrawingStyle(POINT_DRAWING_STYLE);
  const cadDrawingTools = createCadDrawingTools({
    viewer,
    lineEditingPlugin,
    model,
    logger,
  });
  const pointCloudCrsAuthoring = createPointCloudCrsAuthoring({
    context,
    csib: params.csib,
  });
  let disposed = false;

  const elevationSampler = (x: number, y: number): number | undefined =>
    sampleClosestPointCloudElevation(viewer, x, y);
  const transformer = new FeatureCadTransformer({
    projector: cadSceneProjection.projector,
    elevationSampler,
    logger: params.logger,
    pointColor: POINT_STYLE_COLOR,
    lineColor: LINE_STYLE_COLOR,
    resolveLayerStyle: (layerId) =>
      context.editingState.layers.find((layer) => layer.id === layerId)?.style,
  });
  const scenePointFromLonLat: LonLatProjector = (lon, lat) => {
    const scene = cadSceneProjection.converter()?.lonLatToScene(lon, lat);
    return scene ? { x: scene.x, y: scene.y, z: 0 } : undefined;
  };
  const cadGeometryReader = createCadGeometryReader();
  const cadSelectionSync = createCadSelectionSync({
    viewer,
    model,
    context,
    geometryReader: cadGeometryReader,
    logger,
  });
  const featureCadMirror = createFeatureCadMirror({
    viewer,
    model,
    context,
    transformer,
    projection: cadSceneProjection,
  });
  const featureEditingBridge = createFeatureEditingBridge({
    viewer,
    context,
    logger: params.logger,
    getSceneToGeographic: () =>
      cadSceneProjection.converter()
        ? (x, y) =>
            cadSceneProjection.converter()?.sceneToLonLat(x, y) ?? undefined
        : undefined,
    getPointCloudCrsAuthoring: () =>
      pointCloudCrsAuthoring.getPointCloudCrsAuthoring(),
    projectSceneAnchor: scenePointFromLonLat,
  });
  const featureServiceModelLoader = createFeatureServiceModelLoader({
    viewer,
    context,
    logger,
    csib: params.csib,
    geoDataFiles: params.geoDataFiles,
    pointCloudRoots: params.pointCloudRoots,
    projection: cadSceneProjection,
    elevationSampler,
    projectSceneAnchor: scenePointFromLonLat,
  });

  const dispose = (): void => {
    if (disposed) {
      return;
    }
    disposed = true;
    cadSceneProjection.dispose();
    cadSelectionSync.dispose();
    cadDrawingTools.dispose();
    featureEditingBridge.dispose();
    featureServiceModelLoader.dispose();
    featureCadMirror.dispose();
    // unload removes the registry entry so remount can reuse the model id.
    model.clear();
    void viewer
      .unload(CAD_EDITING_MODEL_ID, { fitToView: false })
      .catch((error) => {
        logger.warn(
          'Failed to unload CAD editing model on dispose',
          undefined,
          error instanceof Error ? error : new Error(String(error))
        );
      });
    logger.info('Feature extraction controller disposed');
  };

  const toggleTool = (tool: DrawingTool): void => {
    const allowedTools = context.allowedDrawingTools();
    if (!allowedTools.includes(tool)) {
      logger.warn('Refused drawing tool that does not match active layer', {
        requestedTool: tool,
        allowedTools,
      });
      return;
    }
    cadDrawingTools.toggleTool(tool);
  };

  return {
    activeTool: cadDrawingTools.activeTool,
    toggleTool,
    cancelActiveTool: cadDrawingTools.cancelActiveTool,
    dispose,
  };
}
